/**
 * Jul-2026 (User-Wunsch) — Erweiterung des Bereichs „Boni-Katalog / Hersteller /
 * Händleraktionen" um:
 *   1. Gutschein-Vorlagen: vordefinierte Gutscheine (% oder €), die am Kunden
 *      ausgewählt, zugeordnet und versendet werden können.
 *   2. Rabatt-Regeln: Höchstsätze (% oder €) für Rabatte auf Lohn/Ersatzteile etc.
 * Beides für ALLE Kategorien oder nur spezielle Kategorien erstellbar.
 */
import { useState, useEffect } from "react";
import { Plus, Trash2, Loader2, Percent } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";

export const RULE_CATEGORIES = [
    { k: "labour", l: "Lohn/Arbeit" },
    { k: "part", l: "Ersatzteile" },
    { k: "tire", l: "Reifen" },
    { k: "accessory", l: "Zubehör" },
    { k: "fluid", l: "Flüssigkeiten" },
];
export const catLabel = (k) => RULE_CATEGORIES.find((c) => c.k === k)?.l || k;

function CategoryChips({ selected, setSelected, testid }) {
    const toggle = (k) => setSelected(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]);
    return (
        <div data-testid={testid}>
            <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => setSelected([])}
                    className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border ${selected.length === 0 ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}
                    data-testid={`${testid}-all`}>
                    Alle Kategorien
                </button>
                {RULE_CATEGORIES.map((c) => (
                    <button key={c.k} type="button" onClick={() => toggle(c.k)}
                        className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border ${selected.includes(c.k) ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}
                        data-testid={`${testid}-${c.k}`}>
                        {c.l}
                    </button>
                ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-1">„Alle Kategorien" oder gezielt einzelne Kategorien wählen.</p>
        </div>
    );
}

function UnitToggle({ value, setValue, testid }) {
    return (
        <div className="flex rounded-sm overflow-hidden border border-gray-300 shrink-0" data-testid={testid}>
            <button type="button" onClick={() => setValue("eur")} className={`px-2 text-xs font-bold ${value === "eur" ? "bg-[#111827] text-white" : "bg-white text-gray-500"}`} data-testid={`${testid}-eur`}>€</button>
            <button type="button" onClick={() => setValue("percent")} className={`px-2 text-xs font-bold ${value === "percent" ? "bg-[#111827] text-white" : "bg-white text-gray-500"}`} data-testid={`${testid}-pct`}>%</button>
        </div>
    );
}

const fmtVal = (type, val) => type === "eur"
    ? `${Number(val).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €`
    : `${Number(val).toLocaleString("de-DE")} %`;

export function VoucherTemplatesBlock() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState("");
    const [valueType, setValueType] = useState("percent");
    const [value, setValue] = useState("");
    const [days, setDays] = useState("30");
    const [cats, setCats] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/dealer/voucher-templates")
            .then((r) => setItems(r.data?.templates || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const submit = async () => {
        if (!name.trim()) { toast.error("Bitte einen Namen angeben"); return; }
        const v = Number(value);
        if (!v || v <= 0 || (valueType === "percent" && v > 100)) { toast.error("Bitte einen gültigen Wert angeben"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/dealer/voucher-templates", {
                name: name.trim(), value_type: valueType, value: v,
                valid_days: Number(days) || 30, categories: cats,
            });
            setItems((p) => [...p, data].sort((a, b) => a.name.localeCompare(b.name)));
            setName(""); setValue(""); setDays("30"); setCats([]);
            toast.success("Gutschein-Vorlage angelegt");
        } catch (e) { toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen"); }
        setBusy(false);
    };
    const remove = async (id) => {
        try {
            await api.delete(`/dealer/voucher-templates/${id}`);
            setItems((p) => p.filter((x) => x.id !== id));
        } catch { toast.error("Löschen fehlgeschlagen"); }
    };

    return (
        <div className="border border-pink-200 rounded-sm p-3 space-y-2 bg-pink-50 text-[#111827]" data-testid="voucher-templates-block">
            <h3 className="text-sm font-black text-[#111827] flex items-center gap-1.5">🎁 Gutschein-Vorlagen</h3>
            <p className="text-[11px] text-gray-700">Vordefinierte Gutscheine, die am Kunden ausgewählt, zugeordnet und versendet werden können (% oder €).</p>
            <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[160px]">
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Name</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Treue-Gutschein 10 %" className="mt-1 h-9 rounded-sm" data-testid="vtpl-name" />
                </div>
                <div className="w-40">
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Wert</Label>
                    <div className="flex items-stretch gap-1 mt-1">
                        <Input type="number" min="0" step={valueType === "percent" ? "1" : "0.01"} value={value} onChange={(e) => setValue(e.target.value)} placeholder="Wert" className="h-9 rounded-sm font-mono-num" data-testid="vtpl-value" />
                        <UnitToggle value={valueType} setValue={setValueType} testid="vtpl-unit" />
                    </div>
                </div>
                <div className="w-28">
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Gültig (Tage)</Label>
                    <Input type="number" min="1" max="365" value={days} onChange={(e) => setDays(e.target.value)} className="mt-1 h-9 rounded-sm font-mono-num" data-testid="vtpl-days" />
                </div>
                <Button type="button" onClick={submit} disabled={busy} className="h-9 bg-[#111827] hover:bg-black text-white rounded-sm" data-testid="vtpl-add">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Anlegen</>}
                </Button>
            </div>
            <CategoryChips selected={cats} setSelected={setCats} testid="vtpl-cats" />
            {loading ? (
                <div className="text-xs text-gray-400">Lädt …</div>
            ) : items.length === 0 ? (
                <div className="text-xs text-gray-400 italic" data-testid="vtpl-empty">Noch keine Gutschein-Vorlagen.</div>
            ) : (
                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-sm bg-white" data-testid="vtpl-list">
                    {items.map((it) => (
                        <li key={it.id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`vtpl-item-${it.id}`}>
                            <div className="min-w-0 text-sm">
                                <span className="font-bold">{it.name}</span>
                                <span className="text-gray-500"> · −{fmtVal(it.value_type, it.value)} · {it.valid_days} Tage</span>
                                <span className="text-[10px] text-gray-400 ml-1">
                                    {(it.categories || []).length ? it.categories.map(catLabel).join(", ") : "Alle Kategorien"}
                                </span>
                            </div>
                            <button type="button" onClick={() => remove(it.id)} className="h-8 w-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-sm shrink-0" data-testid={`vtpl-delete-${it.id}`}>
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export function DiscountRulesBlock() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState("");
    const [maxType, setMaxType] = useState("percent");
    const [maxValue, setMaxValue] = useState("");
    const [cats, setCats] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/dealer/discount-rules")
            .then((r) => setItems(r.data?.rules || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const submit = async () => {
        if (!name.trim()) { toast.error("Bitte einen Namen angeben"); return; }
        const v = Number(maxValue);
        if (!v || v <= 0 || (maxType === "percent" && v > 100)) { toast.error("Bitte einen gültigen Höchstsatz angeben"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/dealer/discount-rules", {
                name: name.trim(), max_type: maxType, max_value: v, categories: cats,
            });
            setItems((p) => [...p, data].sort((a, b) => a.name.localeCompare(b.name)));
            setName(""); setMaxValue(""); setCats([]);
            toast.success("Rabatt-Regel angelegt");
        } catch (e) { toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen"); }
        setBusy(false);
    };
    const remove = async (id) => {
        try {
            await api.delete(`/dealer/discount-rules/${id}`);
            setItems((p) => p.filter((x) => x.id !== id));
        } catch { toast.error("Löschen fehlgeschlagen"); }
    };

    return (
        <div className="border border-sky-200 rounded-sm p-3 space-y-2 bg-sky-50 text-[#111827]" data-testid="discount-rules-block">
            <h3 className="text-sm font-black text-[#111827] flex items-center gap-1.5"><Percent className="w-4 h-4" /> Rabatt-Regeln (Höchstsätze)</h3>
            <p className="text-[11px] text-gray-700">Begrenzt Rabatte auf Positionen (z. B. Lohn oder Ersatzteile) auf einen Höchstsatz — in % oder €. Wird bei Werkstatt-Aufträgen &amp; Rechnungen automatisch angewendet.</p>
            <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[160px]">
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Name</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Max. Rabatt Lohn" className="mt-1 h-9 rounded-sm" data-testid="drule-name" />
                </div>
                <div className="w-44">
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Höchstsatz</Label>
                    <div className="flex items-stretch gap-1 mt-1">
                        <Input type="number" min="0" step={maxType === "percent" ? "1" : "0.01"} value={maxValue} onChange={(e) => setMaxValue(e.target.value)} placeholder="max." className="h-9 rounded-sm font-mono-num" data-testid="drule-value" />
                        <UnitToggle value={maxType} setValue={setMaxType} testid="drule-unit" />
                    </div>
                </div>
                <Button type="button" onClick={submit} disabled={busy} className="h-9 bg-[#111827] hover:bg-black text-white rounded-sm" data-testid="drule-add">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Anlegen</>}
                </Button>
            </div>
            <CategoryChips selected={cats} setSelected={setCats} testid="drule-cats" />
            {loading ? (
                <div className="text-xs text-gray-400">Lädt …</div>
            ) : items.length === 0 ? (
                <div className="text-xs text-gray-400 italic" data-testid="drule-empty">Noch keine Rabatt-Regeln.</div>
            ) : (
                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-sm bg-white text-[#111827]" data-testid="drule-list">
                    {items.map((it) => (
                        <li key={it.id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`drule-item-${it.id}`}>
                            <div className="min-w-0 text-sm">
                                <span className="font-bold">{it.name}</span>
                                <span className="text-gray-500"> · max. {fmtVal(it.max_type, it.max_value)}</span>
                                <span className="text-[10px] text-gray-400 ml-1">
                                    {(it.categories || []).length ? it.categories.map(catLabel).join(", ") : "Alle Kategorien"}
                                </span>
                            </div>
                            <button type="button" onClick={() => remove(it.id)} className="h-8 w-8 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-sm shrink-0" data-testid={`drule-delete-${it.id}`}>
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export function VoucherDiscountSettings() {
    // Jul-2026 (User-Wunsch) — Rabatt-Regeln (Höchstsätze) wurden in die
    // VK-Kalkulation (Warenwirtschaft) verschoben; hier bleiben die Gutscheine.
    return (
        <div className="space-y-3 mt-4" data-testid="voucher-discount-settings">
            <VoucherTemplatesBlock />
        </div>
    );
}
